import jsPDF from 'jspdf';
import 'jspdf-autotable';

/**
 * Shared async utility to generate a payslip PDF on the client side.
 * Uses company logo from /logo.png and REACT_APP_COMPANY_NAME env variable.
 *
 * @param {Object} payslip - The payslip data object
 * @returns {Promise<jsPDF>} - The generated jsPDF document instance
 */
export const generatePayslipPDF = async (payslip) => {
    const doc = new jsPDF();

    // Check if autoTable is available
    const useAutoTable = typeof doc.autoTable === 'function';
    if (!useAutoTable) {
        console.warn('jspdf-autotable not available, using manual table drawing');
    }

    // Helper function to format date
    const formatDateForPDF = (date) => {
        if (!date) return 'N/A';
        try {
            const d = new Date(date);
            if (Number.isNaN(d.getTime())) return 'N/A';
            const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            return `${d.getDate()} ${months[d.getMonth()]}, ${d.getFullYear()}`;
        } catch (error) {
            return 'N/A';
        }
    };

    // Helper function to format period string for PDF
    const formatPeriodForPDF = (periodString) => {
        if (!periodString) return 'N/A';

        // Parse period string like "01/11/2025 - 30/11/2025"
        const parts = periodString.split(' - ');
        if (parts.length !== 2) return periodString;

        try {
            const parseDate = (dateStr) => {
                const [day, month, year] = dateStr.trim().split('/');
                return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
            };

            const startDate = parseDate(parts[0]);
            const endDate = parseDate(parts[1]);

            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                return periodString;
            }

            const startFormatted = formatDateForPDF(startDate);
            const endFormatted = formatDateForPDF(endDate);

            return `${startFormatted} - ${endFormatted}`;
        } catch (error) {
            return periodString;
        }
    };

    // Use ONLY salaryStructure.components to determine types
    const salaryStructureComponents = payslip?.salaryStructure?.components || [];
    const componentTypeMap = new Map();

    salaryStructureComponents.forEach(comp => {
        if (comp.name) {
            componentTypeMap.set(comp.name, comp.type);
        }
    });

    const allPayslipComponents = [
        ...(payslip?.components?.earnings || []),
        ...(payslip?.components?.deductions || []),
        ...(payslip?.components?.reimbursements || [])
    ];

    const getComponentType = (name) => {
        return componentTypeMap.get(name) || 'earning';
    };

    // Separate components based on salary structure type only
    const actualEarnings = [];
    const reimbursements = [];
    const actualDeductions = [];

    allPayslipComponents.forEach(comp => {
        const name = comp.name;
        const type = getComponentType(name);
        const amount = comp.amount || 0;

        if (type && amount > 0) {
            if (type === 'earning') {
                actualEarnings.push({ name, amount });
            } else if (type === 'reimbursement') {
                reimbursements.push({ name, amount });
            } else if (type === 'deduction') {
                actualDeductions.push({ name, amount });
            }
        }
    });

    // Calculate totals
    const calculatedTotalEarnings = actualEarnings.reduce((sum, earning) => sum + (earning.amount || 0), 0);
    const calculatedTotalReimbursements = reimbursements.reduce((sum, r) => sum + (r.amount || 0), 0);
    const calculatedTotalDeductions = actualDeductions.reduce((sum, d) => sum + (d.amount || 0), 0);

    // Check if salary structure exists
    const hasSalaryStructure = payslip?.salaryStructure?.components?.length > 0;

    // Calculate monthly amounts
    const monthlyEarnings = hasSalaryStructure
        ? Math.round((calculatedTotalEarnings / 12) * 100) / 100
        : calculatedTotalEarnings;

    const monthlyReimbursements = hasSalaryStructure
        ? Math.round((calculatedTotalReimbursements / 12) * 100) / 100
        : calculatedTotalReimbursements;

    const monthlyTotalDeductions = hasSalaryStructure
        ? Math.round((calculatedTotalDeductions / 12) * 100) / 100
        : calculatedTotalDeductions;

    // Calculate pro-rata ratio
    const presentDays = payslip?.presentDays || 0;
    const workingDays = payslip?.workingDays || 1;
    const proRataRatio = workingDays > 0 ? presentDays / workingDays : 1;

    // Pro-rated amounts
    const proRatedMonthlyEarnings = Math.round((monthlyEarnings * proRataRatio) * 100) / 100;
    const proRatedMonthlyReimbursements = Math.round((monthlyReimbursements * proRataRatio) * 100) / 100;
    const proRatedTotalEarningsAndReimbursements = Math.round((proRatedMonthlyEarnings + proRatedMonthlyReimbursements) * 100) / 100;
    const proRatedMonthlyTotalDeductions = Math.round((monthlyTotalDeductions * proRataRatio) * 100) / 100;

    // Gross Pay = Earnings + Reimbursements + Deductions
    const proRatedMonthlyGrossPay = Math.round((proRatedTotalEarningsAndReimbursements + proRatedMonthlyTotalDeductions) * 100) / 100;

    // Actual pay for this month
    const actualPayThisMonth = payslip?.netPay || 0;

    // Colors (black and white theme)
    const textColor = [0, 0, 0];
    const headerBgColor = [240, 240, 240];
    const borderColor = [200, 200, 200];

    let yPos = 15;

    // Header background
    doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
    doc.rect(0, 0, 210, 35, 'F');

    // Load Company Logo
    try {
        const companyLogoSrc = '/logo.png';
        const drawLogo = new Promise((resolve, reject) => {
            const img = new Image();
            img.src = companyLogoSrc;
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('Failed to load logo'));
        });
        const logoImg = await drawLogo;
        doc.addImage(logoImg, 'PNG', 12, 5, 25, 25);
    } catch (err) {
        console.warn('Could not load logo image for PDF:', err);
    }

    doc.setFontSize(18);
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.setFont('helvetica', 'bold');

    // Dynamic company name from ENV variable
    const companyName = process.env.REACT_APP_COMPANY_NAME || 'Company';
    doc.text(companyName, 105, 15, { align: 'center' });

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const periodFormattedHeader = formatPeriodForPDF(payslip?.period);
    const periodText = `PAYSLIP FOR ${periodFormattedHeader.toUpperCase()}`;
    doc.text(periodText, 105, 23, { align: 'center' });

    yPos = 42;

    // ── Employee Information Table ──────────────────────────────────────────────
    const employeeInfoData = [
        ['Employee Name', payslip?.employeeName || 'N/A', 'Employee ID', payslip?.employeeId || 'N/A'],
        ['Department', payslip?.department || 'N/A', 'Designation', payslip?.designation || 'N/A'],
        ['Pay Period', formatPeriodForPDF(payslip?.period), 'Joining Date', formatDateForPDF(payslip?.joinDate)],
        ['PAN', payslip?.pan || 'N/A', 'Bank A/C No', payslip?.bankAccount || 'N/A'],
        ['UAN', payslip?.uan || 'N/A', 'Bank Name', payslip?.bankName || 'N/A'],
        ['IFSC Code', payslip?.ifsc || 'N/A', 'Status', (payslip?.status || 'N/A').toUpperCase()],
    ];

    if (payslip?.presentDays !== undefined && payslip?.workingDays !== undefined) {
        employeeInfoData.push([
            'Working Days',
            `${payslip.presentDays} / ${payslip.workingDays}`,
            '',
            ''
        ]);
    }

    try {
        if (useAutoTable) {
            doc.autoTable({
                startY: yPos,
                head: [['EMPLOYEE INFORMATION', '', '', '']],
                body: employeeInfoData,
                theme: 'plain',
                headStyles: {
                    fillColor: headerBgColor,
                    textColor: textColor,
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'left',
                    cellPadding: { top: 4, bottom: 6, left: 3, right: 3 }
                },
                bodyStyles: {
                    textColor: textColor,
                    fontSize: 9,
                    cellPadding: 4,
                    overflow: 'linebreak'
                },
                columnStyles: {
                    0: { cellWidth: 38, fontStyle: 'bold' },
                    1: { cellWidth: 47 },
                    2: { cellWidth: 38, fontStyle: 'bold' },
                    3: { cellWidth: 47 }
                },
                margin: { left: 20, right: 20 },
                styles: {
                    lineColor: borderColor,
                    lineWidth: 0.5,
                    cellPadding: 3,
                    overflow: 'linebreak'
                }
            });
            yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : yPos + 30;
        } else {
            // Fallback: Draw table manually with text wrapping
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
            doc.rect(20, yPos, 170, 6, 'F');
            doc.text('EMPLOYEE INFORMATION', 25, yPos + 4);
            yPos += 10;

            doc.setFontSize(9);
            employeeInfoData.forEach((row, rowIndex) => {
                doc.setFont('helvetica', 'bold');
                doc.text(row[0] + ':', 20, yPos);
                doc.setFont('helvetica', 'normal');
                const valueStartX = 58;

                if (rowIndex === 0 || rowIndex === 1) {
                    const maxWidth = 105 - valueStartX - 2;
                    const nameLines = doc.splitTextToSize(row[1], maxWidth);
                    doc.text(nameLines, valueStartX, yPos);
                    const nameHeight = nameLines.length * 4;

                    doc.setFont('helvetica', 'bold');
                    doc.text(row[2] + ':', 105, yPos);
                    doc.setFont('helvetica', 'normal');
                    const value2StartX = 143;
                    const maxWidth2 = 190 - value2StartX - 2;
                    const valueLines = doc.splitTextToSize(row[3], maxWidth2);
                    doc.text(valueLines, value2StartX, yPos);
                    const valueHeight = valueLines.length * 4;

                    yPos += Math.max(nameHeight, valueHeight, 6);
                } else {
                    doc.text(row[1], valueStartX, yPos);
                    doc.setFont('helvetica', 'bold');
                    doc.text(row[2] + ':', 105, yPos);
                    doc.setFont('helvetica', 'normal');
                    doc.text(row[3], 143, yPos);
                    yPos += 6;
                }
            });
            yPos += 5;
        }
    } catch (error) {
        console.error('Error generating employee info table:', error);
        yPos += 30;
    }

    // ── Earnings & Reimbursements Table ────────────────────────────────────────
    const earningsReimbursementsData = [];

    actualEarnings.forEach(earning => {
        let monthlyAmount = hasSalaryStructure
            ? Math.round((earning.amount / 12) * 100) / 100
            : earning.amount;
        monthlyAmount = Math.round((monthlyAmount * proRataRatio) * 100) / 100;
        earningsReimbursementsData.push([
            earning.name || 'N/A',
            `Rs ${monthlyAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ]);
    });

    reimbursements.forEach(reimbursement => {
        let monthlyAmount = hasSalaryStructure
            ? Math.round((reimbursement.amount / 12) * 100) / 100
            : reimbursement.amount;
        monthlyAmount = Math.round((monthlyAmount * proRataRatio) * 100) / 100;
        earningsReimbursementsData.push([
            reimbursement.name || 'N/A',
            `Rs ${monthlyAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ]);
    });

    if (earningsReimbursementsData.length === 0) {
        earningsReimbursementsData.push(['No earnings or reimbursements listed', '']);
    }

    earningsReimbursementsData.push([
        'Total Earnings + Reimbursements',
        `Rs ${proRatedTotalEarningsAndReimbursements.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);

    try {
        if (useAutoTable) {
            doc.autoTable({
                startY: yPos,
                head: [['EARNINGS & REIMBURSEMENTS', '']],
                body: earningsReimbursementsData,
                theme: 'plain',
                headStyles: {
                    fillColor: headerBgColor,
                    textColor: textColor,
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'left',
                    cellPadding: { top: 6, bottom: 8, left: 3, right: 3 }
                },
                bodyStyles: { textColor: textColor, fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 70, halign: 'right' }
                },
                margin: { left: 20, right: 20 },
                styles: { lineColor: borderColor, lineWidth: 0.5, overflow: 'linebreak' },
                didParseCell: function (data) {
                    if (data.row.index === earningsReimbursementsData.length - 1) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = headerBgColor;
                    }
                }
            });
            yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : yPos + 30;
        } else {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
            doc.rect(20, yPos, 170, 8, 'F');
            doc.text('EARNINGS & REIMBURSEMENTS', 25, yPos + 5);
            yPos += 12;
            doc.setFontSize(9);
            earningsReimbursementsData.forEach((row, index) => {
                const isLast = index === earningsReimbursementsData.length - 1;
                doc.setFont('helvetica', isLast ? 'bold' : 'normal');
                if (isLast) {
                    doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
                    doc.rect(20, yPos - 3, 170, 5, 'F');
                }
                doc.setTextColor(textColor[0], textColor[1], textColor[2]);
                doc.text(row[0], 25, yPos);
                doc.text(row[1], 185, yPos, { align: 'right' });
                yPos += 6;
            });
            yPos += 5;
        }
    } catch (error) {
        console.error('Error generating earnings table:', error);
        yPos += 30;
    }

    // ── Deductions Table ────────────────────────────────────────────────────────
    const deductionsTableData = actualDeductions.length > 0
        ? actualDeductions.map(deduction => {
            let monthlyAmount = hasSalaryStructure
                ? Math.round((deduction.amount / 12) * 100) / 100
                : deduction.amount;
            monthlyAmount = Math.round((monthlyAmount * proRataRatio) * 100) / 100;
            return [
                deduction.name || 'N/A',
                `Rs ${monthlyAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            ];
        })
        : [['No deductions listed', '']];

    deductionsTableData.push([
        'Total Deductions',
        `Rs ${proRatedMonthlyTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);

    try {
        if (useAutoTable) {
            doc.autoTable({
                startY: yPos,
                head: [['DEDUCTIONS', '']],
                body: deductionsTableData,
                theme: 'plain',
                headStyles: {
                    fillColor: headerBgColor,
                    textColor: textColor,
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'left',
                    cellPadding: { top: 6, bottom: 8, left: 3, right: 3 }
                },
                bodyStyles: { textColor: textColor, fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
                columnStyles: {
                    0: { cellWidth: 100 },
                    1: { cellWidth: 70, halign: 'right' }
                },
                margin: { left: 20, right: 20 },
                styles: { lineColor: borderColor, lineWidth: 0.5, overflow: 'linebreak' },
                didParseCell: function (data) {
                    if (data.row.index === deductionsTableData.length - 1) {
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.fillColor = headerBgColor;
                    }
                }
            });
            yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : yPos + 30;
        } else {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
            doc.rect(20, yPos, 170, 8, 'F');
            doc.text('DEDUCTIONS', 25, yPos + 5);
            yPos += 12;
            doc.setFontSize(9);
            deductionsTableData.forEach((row, index) => {
                const isLast = index === deductionsTableData.length - 1;
                doc.setFont('helvetica', isLast ? 'bold' : 'normal');
                if (isLast) {
                    doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
                    doc.rect(20, yPos - 3, 170, 5, 'F');
                }
                doc.setTextColor(textColor[0], textColor[1], textColor[2]);
                doc.text(row[0], 25, yPos);
                doc.text(row[1], 185, yPos, { align: 'right' });
                yPos += 6;
            });
            yPos += 5;
        }
    } catch (error) {
        console.error('Error generating deductions table:', error);
        yPos += 30;
    }

    // ── Pay Summary Table ───────────────────────────────────────────────────────
    const paySummaryData = [
        [
            'Gross Pay (Earnings + Reimbursements + Deductions)',
            `Rs ${proRatedMonthlyGrossPay.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ],
        [
            'Total Deductions',
            `Rs ${proRatedMonthlyTotalDeductions.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ],
        [
            'TOTAL NET PAY (For the Month)',
            `Rs ${actualPayThisMonth.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ]
    ];

    // Add incremental info if available
    if (payslip.paidPreviously > 0) {
        paySummaryData.push([
            'Less: Paid Previously',
            `Rs ${payslip.paidPreviously.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ]);
    }

    if (payslip.holdAmount > 0) {
        paySummaryData.push([
            'Less: Amount on Hold',
            `Rs ${payslip.holdAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ]);
    }

    if (payslip.adjustment !== 0) {
        paySummaryData.push([
            'Adjustment',
            `${payslip.adjustment >= 0 ? '+' : ''} Rs ${payslip.adjustment.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        ]);
    }

    const netPayableNow = payslip.paidAmount || payslip.finalPay || actualPayThisMonth;
    paySummaryData.push([
        'NET PAYABLE (Amount for this Batch)',
        `Rs ${netPayableNow.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    ]);

    if (presentDays > 0 && workingDays > 0) {
        paySummaryData.push([`Based on ${presentDays} working days out of ${workingDays} days`, '']);
    }

    try {
        if (useAutoTable) {
            doc.autoTable({
                startY: yPos,
                head: [['PAY SUMMARY', '']],
                body: paySummaryData,
                theme: 'plain',
                headStyles: {
                    fillColor: headerBgColor,
                    textColor: textColor,
                    fontStyle: 'bold',
                    fontSize: 10,
                    halign: 'left',
                    cellPadding: { top: 6, bottom: 8, left: 3, right: 3 }
                },
                bodyStyles: { textColor: textColor, fontSize: 9, cellPadding: 3, overflow: 'linebreak' },
                columnStyles: {
                    0: { cellWidth: 100, fontStyle: 'normal' },
                    1: { cellWidth: 70, halign: 'right' }
                },
                margin: { left: 20, right: 20 },
                styles: { lineColor: borderColor, lineWidth: 0.5, overflow: 'linebreak' },
                didParseCell: function (data) {
                    if (data.row.index === 2) {
                        if (data.column.index === 0) {
                            data.cell.colSpan = 2;
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fontSize = 11;
                            data.cell.styles.fillColor = headerBgColor;
                            data.cell.styles.halign = 'center';
                            data.cell.text = paySummaryData[2][0] + ' ' + paySummaryData[2][1];
                        } else {
                            data.cell.text = '';
                        }
                    } else if (data.row.index === paySummaryData.length - 1) {
                        if (data.column.index === 0) {
                            data.cell.colSpan = 2;
                            data.cell.styles.fontStyle = 'bold';
                            data.cell.styles.fontSize = 12;
                            data.cell.styles.fillColor = [220, 252, 231]; // Light green for success
                            data.cell.styles.halign = 'center';
                            data.cell.text = paySummaryData[paySummaryData.length - 1][0] + ' ' + paySummaryData[paySummaryData.length - 1][1];
                        } else {
                            data.cell.text = '';
                        }
                    } else if (data.row.index === paySummaryData.length - 2 && paySummaryData.length > 4) {
                        // Metadata row or other
                        data.cell.styles.fontSize = 7;
                    }
                }
            });
            yPos = doc.lastAutoTable?.finalY ? doc.lastAutoTable.finalY + 8 : yPos + 30;
        } else {
            doc.setFontSize(10);
            doc.setFont('helvetica', 'bold');
            doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
            doc.rect(20, yPos, 170, 8, 'F');
            doc.text('PAY SUMMARY', 25, yPos + 5);
            yPos += 12;
            doc.setFontSize(9);
            paySummaryData.forEach((row, index) => {
                const isNetPay = index === 2;
                const isLast = index === paySummaryData.length - 1;

                if (isNetPay) {
                    doc.setFillColor(headerBgColor[0], headerBgColor[1], headerBgColor[2]);
                    doc.rect(20, yPos - 3, 170, 7, 'F');
                    doc.setFontSize(11);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
                    doc.text(row[0] + ' ' + row[1], 105, yPos, { align: 'center' });
                    yPos += 7;
                } else if (isLast) {
                    doc.setFontSize(7);
                    doc.setTextColor(128, 128, 128);
                    doc.setFont('helvetica', 'normal');
                    doc.text(row[0], 105, yPos, { align: 'center' });
                    yPos += 4;
                } else {
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'normal');
                    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
                    doc.text(row[0], 25, yPos);
                    if (row[1]) doc.text(row[1], 185, yPos, { align: 'right' });
                    yPos += 6;
                }
            });
            yPos += 5;
        }
    } catch (error) {
        console.error('Error generating pay summary table:', error);
        yPos += 30;
    }

    // ── Footer ──────────────────────────────────────────────────────────────────
    yPos += 5;
    doc.setFontSize(8);
    doc.setTextColor(128, 128, 128);
    doc.setFont('helvetica', 'normal');
    doc.text('This is a computer-generated payslip and does not require a signature.', 105, yPos, { align: 'center' });
    yPos += 6;

    const dateValue = payslip?.generatedDate || payslip?.createdAt;
    const generatedDateText = dateValue ? formatDateForPDF(dateValue) : 'N/A';
    doc.text(`Generated on: ${generatedDateText}`, 105, yPos, { align: 'center' });

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(128, 128, 128);
        doc.text(
            `Page ${i} of ${pageCount}`,
            105,
            doc.internal.pageSize.height - 10,
            { align: 'center' }
        );
    }

    return doc;
};
